import type { AriaAttributes, KeyboardEvent, Ref } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import type { ClassNameProp } from '../common-types';
import { Direction } from '../common-types';
import type { CommonInputProps } from './CommonInputProps';
import type { ValidIconName } from '../basic';
import { Icon } from '../basic';
import { noPropagationClickHandler } from '../../lib';
import { ValidationErrorMessage } from './internal';

/**
 * Props for the {@link Toggle} component.
 *
 */
export interface CheckboxProps
  extends ClassNameProp,
    CommonInputProps<boolean>,
    Pick<AriaAttributes, 'aria-label' | 'aria-describedby'> {
  /**
   * An optional custom color to use when the input is active.
   *
   * This is not supported by IE - if you need it outside the patient portal, use the classes as documented in {@link Toggle} and {@link Checkbox}.
   * Cannot be used together with {@link neutral}.
   */
  color?: string;
  /**
   * An optional label component that may render more complex sub-components (like hyperlinks inside the label).
   *
   * This has to be seen as possible extension for the regular label (which is always used as aria-label text).
   */
  labelComponent?: React.JSX.Element;
  /**
   * Custom icon for checked value.
   */
  checkedIcon?: ValidIconName;
  /**
   * Custom icon for unchecked value.
   */
  uncheckedIcon?: ValidIconName;
  /**
   * Placement of the checkbox label.
   */
  labelPlacement?: Direction;
  /**
   * Whether checkbox should be in indeterminate state
   */
  indeterminate?: boolean;
  /**
   * Defines whether the checkbox should become active when clicked on intermediate state
   */
  setCheckedOnIndeterminateClick?: boolean;
  /**
   * If true, the active state will be gray instead of the default color.
   * Overrides {@link color} if used together.
   */
  neutral?: boolean;
}

/**
 * A Material Design Toggle component.
 *
 */
const BooleanInput = function BooleanInput({
  debounceWait = 1000,
  indeterminate = false,
  setCheckedOnIndeterminateClick = true,
  defaultValue,
  value,
  onChange,
  onFocus,
  onBlur,
  readOnly,
  className: classNameProp,
  label: labelProp,
  labelPlacement = Direction.RIGHT,
  labelComponent,
  required,
  disabled,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  error,
  color,
  mode,
  checkedIcon: checkedIconProp,
  uncheckedIcon: uncheckedIconProp,
  neutral = false,
  ...props
}: Readonly<CheckboxProps> & {
  mode: 'toggle' | 'checkbox';
  indeterminate?: boolean;
} & {
  ref?: Ref<HTMLInputElement>;
}): React.JSX.Element {
  const controlled = value !== undefined;
  const [state, setState] = useState(value ?? defaultValue);
  useEffect(() => {
    if (controlled) {
      setState(value);
    }
  }, [controlled, value]);

  const currentState = controlled ? !!value : !!state;

  // decorate change callback event with debounce if necessary
  let onChangeHandler = onChange;
  if (!controlled && onChange && debounceWait > 0) {
    onChangeHandler = _.debounce(onChange, debounceWait);
  }

  const changeHandler = useCallback(() => {
    if (disabled || readOnly) {
      return;
    }
    const newState =
      !indeterminate || (indeterminate && !setCheckedOnIndeterminateClick)
        ? !currentState
        : currentState;
    if (!controlled) {
      setState(newState);
    }
    onChangeHandler?.(newState);
  }, [
    controlled,
    currentState,
    disabled,
    indeterminate,
    onChangeHandler,
    readOnly,
    setCheckedOnIndeterminateClick
  ]);

  let wrapperClassName = `ehs-${mode}-wrapper`;
  if (classNameProp) wrapperClassName += ` ${classNameProp}`;

  const label = useMemo(() => (required ? `${labelProp} *` : labelProp), [
    labelProp,
    required
  ]);

  let className = `ehs-${mode} label-${labelPlacement}`;
  if (error) className += ' invalid';
  else className += ' valid';
  if (disabled) className += ' disabled';
  if (neutral) {
    className += ' neutral';
  }

  // support custom colors
  let boxStyle;
  let handleStyle;
  let trackStyle;
  if (!neutral) {
    boxStyle = currentState && color ? { color: color } : undefined;
    handleStyle =
      currentState && color ? { backgroundColor: color } : undefined;
    trackStyle =
      currentState && color ? { ...handleStyle, opacity: 0.3 } : undefined;
  }

  const icon = useMemo(() => {
    const uncheckedIcon =
      uncheckedIconProp || 'materialicon-check_box_outline_blank';
    const checkedIcon = checkedIconProp || 'materialicon-check_box';
    const indeterminateIcon = 'materialicon-indeterminate_check_box';

    if (!currentState) {
      return uncheckedIcon;
    } else if (indeterminate) {
      return indeterminateIcon;
    } else {
      return checkedIcon;
    }
  }, [checkedIconProp, currentState, indeterminate, uncheckedIconProp]);

  const onClickHandler = useMemo(
    () => noPropagationClickHandler(changeHandler),
    [changeHandler]
  );
  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.stopPropagation();
        changeHandler();
      }
    },
    [changeHandler]
  );
  const ariaDescribedByIds = [ariaDescribedBy];
  let errorId = '';
  if (error) {
    errorId = _.uniqueId('ehs-toggle-input-error-');
    ariaDescribedByIds.push(errorId);
  }

  return (
    <div {...props} className={wrapperClassName}>
      <div
        className={className}
        onBlur={onBlur}
        onFocus={onFocus}
        onClick={onClickHandler}
        onKeyDown={onKeyDownHandler}
      >
        <span>
          <input
            ref={props.ref}
            type="checkbox"
            disabled={disabled}
            readOnly={readOnly}
            onChange={_.noop}
            defaultChecked={defaultValue}
            checked={
              value === undefined || indeterminate ? undefined : currentState
            }
            aria-required={required}
            aria-readonly={readOnly}
            aria-invalid={!!error}
            aria-label={ariaLabel ?? label}
            aria-checked={
              currentState && indeterminate ? 'mixed' : currentState
            }
            aria-describedby={ariaDescribedByIds.join(' ').trim() || undefined}
          />

          {mode === 'toggle' && (
            <>
              <span
                style={trackStyle}
                className={`track ${currentState ? 'active' : ''}`}
              />
              <span
                style={handleStyle}
                className={`handle ${currentState ? 'active' : ''}`}
              />
            </>
          )}

          {mode === 'checkbox' && (
            <Icon
              style={boxStyle}
              aria-label={undefined}
              className={`box ${currentState ? 'active' : ''}`.trim()}
              iconName={icon}
            />
          )}
        </span>

        <label aria-hidden={true}>{labelComponent ?? label}</label>
      </div>
      {error && <ValidationErrorMessage id={errorId} error={error} />}
    </div>
  );
};

/**
 * A Material Design Toggle component.
 *
 * If you want to set the color using a css class like in the health record, override the following:
 *
 * ```
 * .toggle .handle.active { background-color: ... }
 * .toggle .track.active { opacity: 0.3; background-color: ...}
 * ```
 *
 */
export const Toggle = function Toggle(
  props: Readonly<
    Omit<
      CheckboxProps,
      | 'checkedIcon'
      | 'uncheckedIcon'
      | 'indeterminate'
      | 'setCheckedOnIndeterminateClick'
    >
  > & {
    ref?: Ref<HTMLInputElement>;
  }
): React.JSX.Element {
  return <BooleanInput {...props} mode="toggle" ref={props.ref} />;
};

/**
 * A Material Design Checkbox component.
 *
 * If you want to set the color using a css class like in the health record, override the following:
 *
 * ```
 * .checkbox .box.active { color: ... }
 * ```
 *
 */
export const Checkbox = function Checkbox(
  props: Readonly<CheckboxProps> & {
    ref?: Ref<HTMLInputElement>;
  }
): React.JSX.Element {
  return (
    <BooleanInput
      {...props}
      defaultValue={props.defaultValue ?? false}
      mode="checkbox"
      ref={props.ref}
    />
  );
};
